import { useState, useEffect, useRef, useMemo } from "react";
import { createPurchaseOrder } from "../../services/api/boq/purchaseService";
import { fetchProjectsApi } from "../../services/api/boq/projectService";
import { getVendors } from "../../services/api/boq/VendorService";
import { fetchClientsApi } from "../../services/api/boq/clientService";
import {
  fetchBoqByProject,
  fetchBoqById,
} from "../../services/api/boq/boqService";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Modal from "react-modal";
import { FaEye } from "react-icons/fa";

export default function CreatePurchaseOrder() {
  const generatePONumber = () => {
    return `#PO-${crypto.randomUUID().slice(0, 8).toUpperCase()}`;
  };

  const today = new Date().toISOString().split("T")[0];

  const exp_today = new Date().toISOString().split("T")[0];

  const [poData, setPoData] = useState({
    vendor_id: "",
    project_id: "",
    po_number: generatePONumber(),
    order_date: today,
    total_amount: 0,
    gst_amount: 0,
    t_c: "",
    notes: "",
    delivery_date: exp_today,
    status: "draft",
    deliver_to: "",
    items: [],
  });

  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [boqs, setBoqs] = useState([]);
  const [alphaFilter, setAlphaFilter] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [descModal, setDescModal] = useState(false);
  const [selectedDesc, setSelectedDesc] = useState("");
  const [loadingItems, setLoadingItems] = useState(false);
  const [clients, setClients] = useState([]);
  const manualItemRef = useRef(null);

  // Fetch vendors and projects on mount
  useEffect(() => {
    fetchVendors();
    fetchProjects();
    fetchClients();
  }, []);

  useEffect(() => {
    if (!poData.project_id || clients.length === 0) return;

    const selectedProject = projects.find((p) => p.id === poData.project_id);
    if (!selectedProject) return;

    const selectedClient = clients.find(
      (c) => c.id === selectedProject.client_id,
    );
    if (!selectedClient) return;

    setPoData((prev) => ({
      ...prev,
      deliver_to: selectedClient.in_site_location || "",
    }));
  }, [poData.project_id, clients]);

  const fetchVendors = async () => {
    let page = 1;
    let allVendors = [];
    let lastPage = 1;

    try {
      do {
        const res = await getVendors(page);

        const data = res?.data?.data;
        const vendors = data?.data ?? [];

        allVendors = [...allVendors, ...vendors];

        lastPage = data?.last_page;
        page++;
      } while (page <= lastPage);

      setVendors(allVendors);
    } catch (err) {
      console.error("Error fetching vendors:", err);
    }
  };

  const fetchClients = async () => {
    const res = await fetchClientsApi();
    const clientList = res?.data || [];
    setClients(clientList);
  };

  const fetchProjects = async () => {
    const res = await fetchProjectsApi();
    const projectList = res?.data?.data?.data ?? [];
    setProjects(projectList);
  };

  const alphabetGroups = [
    { label: "All", value: "ALL" },
    { label: "A-D", value: "A-D" },
    { label: "E-H", value: "E-H" },
    { label: "I-L", value: "I-L" },
    { label: "M-P", value: "M-P" },
    { label: "Q-T", value: "Q-T" },
    { label: "U-Z", value: "U-Z" },
  ];

  const filteredItems = useMemo(() => {
    return poData.items.filter((item) => {
      const name = item.item_name?.toLowerCase() || "";

      const matchesSearch =
        !searchTerm.trim() || name.includes(searchTerm.toLowerCase());

      let matchesAlpha = true;

      if (alphaFilter !== "ALL") {
        const [start, end] = alphaFilter.split("-");
        const firstChar = item.item_name?.charAt(0)?.toUpperCase() || "";
        matchesAlpha = firstChar >= start && firstChar <= end;
      }

      return matchesSearch && matchesAlpha;
    });
  }, [poData.items, searchTerm, alphaFilter]);

  const getGroupCount = (groupValue) => {
    if (groupValue === "ALL") return poData.items.length;

    const [start, end] = groupValue.split("-");

    return poData.items.filter((item) => {
      const firstChar = item.item_name?.charAt(0)?.toUpperCase();
      return firstChar >= start && firstChar <= end;
    }).length;
  };

  const handleCancel = () => {
    navigate("/purchase-orders");
  };

  const openDescription = (desc) => {
    setSelectedDesc(desc);
    setDescModal(true);
  };

  const handleAddManualItem = () => {
    const newItem = {
      id: Date.now(),
      boq_item_id: null,
      item_name: "",
      description: "",
      quantity: 0,
      unit: "",
      unit_price: 0,
      ordered_qty: 0.0,
      total: 0,
      deliver_to: "",
      is_manual: 1,
      is_new: true,
      is_billable: 1,
    };

    setPoData((prev) => ({
      ...prev,
      items: [newItem, ...prev.items],
    }));

    setTimeout(() => {
      manualItemRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);

    setTimeout(() => {
      setPoData((prev) => ({
        ...prev,
        items: prev.items.map((i) => (i.is_new ? { ...i, is_new: false } : i)),
      }));
    }, 2000);
  };

  const handleRemoveItem = (id) => {
    if (!window.confirm("Remove this item?")) return;

    const updatedItems = poData.items.filter((item) => item.id !== id);

    const totalAmount = updatedItems.reduce((sum, i) => sum + i.total, 0);

    setPoData({
      ...poData,
      items: updatedItems,
      total_amount: totalAmount,
    });
  };

  const gstRate = 0.18;

  const subtotal = poData.total_amount || 0;
  const gstAmount = +(subtotal * gstRate).toFixed(2);
  const grandTotal = +(subtotal + gstAmount).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!poData.vendor_id || !poData.project_id) {
      alert("Please select Vendor and Project");
      return;
    }
    const invalidManual = poData.items.some(
      (i) =>
        i.is_manual && (!i.item_name || !i.description || i.ordered_qty <= 0),
    );

    if (invalidManual) {
      toast.error("Fill all manual item fields properly");
      return;
    }

    const formattedItems = poData.items
      .filter((i) => i.ordered_qty > 0)
      .map((i) => ({
        boq_item_id: i.boq_item_id,
        item_name: i.item_name,
        quantity: i.quantity,
        description: i.description,
        unit: i.unit,
        ordered_qty: parseFloat(i.ordered_qty) || 0.0,
        unit_price: parseFloat(i.unit_price) || 0.0,
        total: parseFloat(i.total) || 0.0,
        is_billable: i.is_billable || 1,
        ...(i.is_manual ? { is_manual: 1 } : { boq_id: i.boq_item_id }),
      }));

    if (formattedItems.length === 0) {
      toast.error("Enter quantity for at least one item");
      return;
    }

    const payload = {
      vendor_id: poData.vendor_id,
      po_number: poData.po_number,
      project_id: poData.project_id,
      order_date: poData.order_date,
      total_amount: grandTotal,
      gst_amount: gstAmount,
      delivery_date: poData.delivery_date,
      deliver_to: poData.deliver_to,
      status: poData.status,
      t_c: poData.t_c,
      notes: poData.notes,
      items: formattedItems,
    };

    try {
      await createPurchaseOrder(payload);
      toast.success("Purchase Order created successfully!");
      window.location.href = "/purchase-orders";
    } catch (err) {
      console.error(err);
      toast.error("Error creating PO");
    }
  };

  const hasQty = poData.items.some((i) => parseFloat(i.ordered_qty) > 0);

  const updateItem = (id, updates) => {
    setPoData((prev) => {
      const updatedItems = prev.items.map((item) => {
        if (item.id !== id) return item;

        const updated = { ...item, ...updates };

        // auto total calculation
        const qty = parseFloat(updated.ordered_qty || 0);
        const rate = parseFloat(updated.unit_price || 0);
        updated.total = qty * rate;

        return updated;
      });

      const totalAmount = updatedItems.reduce((sum, i) => sum + i.total, 0);

      return {
        ...prev,
        items: updatedItems,
        total_amount: totalAmount,
      };
    });
  };

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <h2 className="text-xl font-bold mb-4">Create Purchase Order</h2>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* PO Number and Date */}
        {/* <div className="flex flex-col md:flex-row gap-2"> */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-200">
          {" "}
          <div className=" flex flex-col w-full md:w-[300px] gap-1">
            <label className="text-sm font-semibold text-gray-700">
              PO Number
            </label>
            <input
              type="text"
              value={poData.po_number}
              onChange={(e) =>
                setPoData({ ...poData, po_number: e.target.value })
              }
              className="border px-3 py-2 rounded w-full"
            />
          </div>
          <div className="flex flex-col gap-1 w-full">
            <label className="text-sm font-semibold text-gray-700">
              PO Date
            </label>
            <input
              type="date"
              value={poData.order_date}
              readOnly
              className="border px-3 py-2 rounded bg-gray-100 cursor-not-allowed"
            />
          </div>
          {/* NEW: Select Client Dropdown */}
          <div className="flex-1">
            <label className="text-sm font-semibold text-gray-700">
              Select Client
            </label>
            <select
              className="border px-3 py-2 rounded w-full focus:ring-2 focus:ring-blue-500 outline-none"
              value={poData.client_id || ""}
              onChange={(e) => {
                const selectedId = Number(e.target.value);
                const client = clients.find((c) => c.id === selectedId);

                setPoData((prev) => ({
                  ...prev,
                  client_id: selectedId,
                  deliver_to: client?.in_site_location || prev.deliver_to,
                }));
              }}
            >
              <option value="">Select Client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}{" "}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Vendor & Project Dropdowns */}
        <div className="flex flex-col md:flex-row gap-2">
          <select
            value={poData.vendor_id}
            onChange={(e) =>
              setPoData({ ...poData, vendor_id: Number(e.target.value) })
            }
            className="border px-3 py-2 rounded flex-1"
            required
          >
            <option value="">Select Vendor</option>

            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
          </select>
          <select
            className="border px-3 py-2 rounded flex-1"
            value={poData.project_id}
            onChange={async (e) => {
              const projectId = Number(e.target.value);

              setPoData((prev) => ({
                ...prev,
                project_id: projectId,
                boq_id: "",
                items: [],
              }));

              if (!projectId) return;

              try {
                const res = await fetchBoqByProject(projectId);

                const boqList = res?.data?.data || res?.data || res || [];
                setBoqs(boqList);
              } catch (error) {
                console.error("BOQ fetch failed:", error);
                setBoqs([]);
              }
            }}
          >
            <option value="">Select Project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.project_name} ({project.project_code})
              </option>
            ))}
          </select>
        </div>

        <div className="mt-6 p-4 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-end">
            {/* BOQ Select */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold text-gray-700">
                Select BOQ
              </label>
              <select
                value={poData.boq_id}
                className="border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                onChange={async (e) => {
                  const boqId = e.target.value;

                  setPoData((prev) => ({
                    ...prev,
                    boq_id: boqId,
                    items: [],
                  }));

                  if (!boqId) return;

                  setLoadingItems(true);

                  try {
                    if (boqId === "all") {
                      let allItems = [];

                      for (const boq of boqs) {
                        const res = await fetchBoqById(boq.id);

                        const boqItems = res?.data?.items || [];

                        const formattedItems = boqItems.map((item) => ({
                          id: item.id,
                          boq_item_id: item.id,
                          item_name: item.item_name,
                          description: item.description || "",
                          quantity: item.quantity || 0,
                          unit: item.unit,
                          unit_price: parseFloat(item.rate || 0),
                          ordered_qty: 0,
                          total: 0,
                          is_billable: item.is_billable || 1,
                          boq_name: boq.boq_name,
                        }));

                        allItems.push(...formattedItems);
                      }

                      allItems = allItems.filter(
                        (item, index, self) =>
                          index ===
                          self.findIndex(
                            (t) =>
                              t.item_name === item.item_name &&
                              t.unit === item.unit &&
                              t.description === item.description,
                          ),
                      );

                      setPoData((prev) => ({
                        ...prev,
                        items: allItems,
                      }));
                    } else {
                      const res = await fetchBoqById(boqId);

                      const boqItems = res?.data?.items || [];

                      const formattedItems = boqItems.map((item) => ({
                        id: item.id,
                        boq_item_id: item.id,
                        item_name: item.item_name,
                        description: item.description || "",
                        quantity: item.quantity || 0,
                        unit: item.unit,
                        unit_price: parseFloat(item.rate || 0),
                        ordered_qty: 0.0,
                        total: 0,
                        is_billable: item.is_billable || 1,
                      }));

                      setPoData((prev) => ({
                        ...prev,
                        items: formattedItems,
                      }));
                    }
                  } catch (err) {
                    console.error("BOQ items load failed", err);
                  } finally {
                    setLoadingItems(false);
                  }
                }}
              >
                <option value="">Select BOQ</option>
                <option value="all">All</option>

                {boqs.map((boq) => (
                  <option key={boq.id} value={boq.id}>
                    {boq.boq_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Delivery To */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold text-gray-700">
                Delivery To
              </label>
              <textarea
                placeholder="Enter delivery address"
                value={poData.deliver_to}
                onChange={(e) =>
                  setPoData({ ...poData, deliver_to: e.target.value })
                }
                rows={2}
                className="border border-gray-300 px-3 py-2 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            {/* Delivery Date */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold text-gray-700">
                Expected Delivery Date
              </label>

              <input
                type="date"
                value={poData.delivery_date}
                onChange={(e) =>
                  setPoData({ ...poData, delivery_date: e.target.value })
                }
                min={exp_today}
                className="border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            type="button"
            onClick={handleAddManualItem}
            className="bg-green-600 text-white px-3 py-1.5 rounded"
          >
            + Add Manual Item
          </button>
        </div>

        {/* Items Section */}
        <h3 className="font-semibold mt-4">BOQ Items</h3>
        {/* 🔎 Search Bar */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search BOQ items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition"
          />
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {alphabetGroups.map((group) => {
            const count = getGroupCount(group.value);

            return (
              <button
                key={group.value}
                type="button"
                onClick={() => setAlphaFilter(group.value)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-all duration-300 transform
          ${
            alphaFilter === group.value
              ? "bg-blue-600 text-white scale-105 shadow-md"
              : "bg-white hover:bg-gray-100 hover:scale-105"
          }`}
              >
                {group.label}
                <span
                  className={`text-xs px-2 py-0.5 rounded-full transition
            ${
              alphaFilter === group.value
                ? "bg-white text-blue-600"
                : "bg-gray-200 text-gray-700"
            }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto border rounded-lg">
          {loadingItems ? (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-500">Loading BOQ Items...</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr className="text-left text-gray-600">
                  <th className="p-3">#</th>
                  <th className="p-3">Item</th>
                  <th className="p-3">Description</th>
                  <th className="p-3">Qty</th>
                  <th className="p-3">Unit</th>
                  <th className="p-3">Rate</th>
                  <th className="p-3">Order Qty</th>
                  <th className="p-3">Billable</th>
                  <th className="p-3">Pending PO Qty</th>
                  <th className="p-3">Total</th>
                  <th className="p-3">Action</th>
                </tr>
              </thead>

              <tbody className="transition-all duration-300">
                {filteredItems.map((item, idx) => (
                  <tr
                    key={idx}
                    ref={item.is_new ? manualItemRef : null}
                    className={`border-t hover:bg-gray-50 transition duration-200 animate-fadeIn text-left 
    ${item.is_new ? "bg-yellow-100" : ""}`}
                  >
                    <td className="p-3">{idx + 1}</td>

                    {/* Item Name */}
                    <td className="p-3">
                      {item.is_manual ? (
                        <input
                          value={item.item_name}
                          onChange={(e) => {
                            const newItems = [...poData.items];
                            const index = poData.items.findIndex(
                              (i) => i.id === item.id,
                            );
                            newItems[index].item_name = e.target.value;

                            setPoData({ ...poData, items: newItems });
                          }}
                          className="border px-2 py-1 rounded w-full"
                        />
                      ) : (
                        item.item_name
                      )}
                    </td>

                    {/* Description */}
                    <td className="p-3">
                      <div className="flex items-start gap-2">
                        <span className="line-clamp-2 break-words">
                          {item.is_manual ? (
                            <input
                              value={item.description}
                              onChange={(e) => {
                                const index = poData.items.findIndex(
                                  (i) => i.id === item.id,
                                );
                                const newItems = [...poData.items];
                                newItems[index].description = e.target.value;

                                setPoData({ ...poData, items: newItems });
                              }}
                              className="border px-2 py-1 rounded w-full"
                            />
                          ) : (
                            <span className="line-clamp-2">
                              {item.description}
                            </span>
                          )}
                        </span>
                        {item.description?.length > 60 && (
                          <button
                            type="button"
                            onClick={() => openDescription(item.description)}
                            className="text-blue-600"
                          >
                            <FaEye />
                          </button>
                        )}
                      </div>
                    </td>

                    {/* BOQ Qty */}
                    <td className="p-3">
                      {item.is_manual ? (
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => {
                            const qty = parseFloat(e.target.value) || 0;
                            const index = poData.items.findIndex(
                              (i) => i.id === item.id,
                            );
                            const newItems = [...poData.items];

                            newItems[index].quantity = qty;

                            setPoData({ ...poData, items: newItems });
                          }}
                          className="border px-2 py-1 rounded w-20"
                        />
                      ) : (
                        item.quantity
                      )}
                    </td>

                    <td className="p-3 ">{item.unit || "-"}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <span>₹</span>
                        <input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            const newItems = [...poData.items];
                            // Find the index in the original poData.items array
                            const index = poData.items.findIndex(
                              (i) => i.id === item.id,
                            );

                            // Update the price and recalculate this item's total
                            newItems[index].unit_price = value;
                            newItems[index].total =
                              value * newItems[index].ordered_qty;

                            // Recalculate the Grand Total
                            const totalAmount = newItems.reduce(
                              (sum, i) => sum + i.total,
                              0,
                            );

                            setPoData({
                              ...poData,
                              items: newItems,
                              total_amount: totalAmount,
                            });
                          }}
                          className="border px-2 py-1 rounded w-24 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                    </td>

                    {/* Ordered Qty */}
                    <td className="p-3">
                      <input
                        type="number"
                        step="0.01"
                        value={item.ordered_qty}
                        min="0"
                        onChange={(e) => {
                          const qty = parseFloat(e.target.value) || 0;
                          const maxQty = item.is_manual
                            ? Infinity
                            : parseFloat(item.quantity);
                          const newItems = [...poData.items];
                          const originalIndex = poData.items.findIndex(
                            (i) => i.id === item.id,
                          );

                          if (qty > maxQty) {
                            newItems[originalIndex].error =
                              `Max allowed: ${maxQty}`;
                            newItems[originalIndex].ordered_qty = qty;
                            setPoData({
                              ...poData,
                              items: newItems,
                            });
                            return;
                          }

                          newItems[originalIndex].error = "";
                          newItems[originalIndex].ordered_qty = qty;
                          newItems[originalIndex].total =
                            qty * newItems[originalIndex].unit_price;

                          const totalAmount = newItems.reduce(
                            (sum, i) => sum + i.total,
                            0,
                          );

                          setPoData({
                            ...poData,
                            items: newItems,
                            total_amount: totalAmount,
                          });
                        }}
                        className={`border px-2 py-1 rounded w-20 ${
                          item.error ? "border-red-500" : ""
                        }`}
                      />
                      {item.error && (
                        <p className="text-red-500 text-xs mt-1">
                          {item.error}
                        </p>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <input
                        type="checkbox"
                        checked={item.is_billable !== 0}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          const newItems = [...poData.items];
                          const index = poData.items.findIndex(
                            (i) => i.id === item.id,
                          );

                          newItems[index].is_billable = isChecked ? 1 : 0;
                          setPoData({ ...poData, items: newItems });
                        }}
                      />
                    </td>

                    <td className="p-3">{item.pending_po_qty || 0}</td>

                    <td className="p-3 font-semibold">₹ {item.total}</td>
                    <td className="p-3">
                      {item.is_manual && (
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="bg-red-500 text-white px-2 py-1 rounded text-xs"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Mobile Card Layout */}
        <div className="md:hidden">
          {loadingItems ? (
            <div className="flex flex-col items-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-500">Loading BOQ Items...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((item, idx) => {
                const originalIndex = poData.items.findIndex(
                  (i) => i.id === item.id,
                );

                return (
                  <div
                    key={idx}
                    className="border rounded-lg p-4 shadow-sm bg-white"
                  >
                    <p className="font-semibold text-sm">
                      {item.is_manual ? (
                        <input
                          type="text"
                          value={item.item_name}
                          onChange={(e) => {
                            const newItems = [...poData.items];
                            const index = poData.items.findIndex(
                              (i) => i.id === item.id,
                            );
                            newItems[index].item_name = e.target.value;

                            setPoData({ ...poData, items: newItems });
                          }}
                          className="border px-2 py-1 rounded w-full"
                        />
                      ) : (
                        item.item_name
                      )}
                    </p>

                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm text-gray-500">Description</span>

                      {item.description?.length > 60 ? (
                        <button
                          type="button"
                          onClick={() => openDescription(item.description)}
                          className="text-blue-600"
                        >
                          <FaEye />
                        </button>
                      ) : (
                        <span className="text-sm">
                          {item.is_manual ? (
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => {
                                const index = poData.items.findIndex(
                                  (i) => i.id === item.id,
                                );
                                const newItems = [...poData.items];
                                newItems[index].description = e.target.value;

                                setPoData({ ...poData, items: newItems });
                              }}
                              className="border px-2 py-1 rounded w-full"
                              placeholder="Enter description"
                            />
                          ) : (
                            <span className="line-clamp-2">
                              {item.description}
                            </span>
                          )}
                        </span>
                      )}
                    </div>

                    <div className="flex justify-between text-sm mt-2">
                      <span>BOQ Qty</span>
                      <span>
                        {item.is_manual ? (
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => {
                              const qty = parseFloat(e.target.value) || 0;
                              const index = poData.items.findIndex(
                                (i) => i.id === item.id,
                              );
                              const newItems = [...poData.items];

                              newItems[index].quantity = qty;

                              setPoData({ ...poData, items: newItems });
                            }}
                            className="border px-2 py-1 rounded w-20"
                          />
                        ) : (
                          item.quantity || 0
                        )}
                      </span>
                    </div>

                    <div className="flex justify-between text-sm">
                      <span>Unit</span>
                      <span>{item.unit || "-"}</span>
                    </div>

                    <div className="flex justify-between text-sm">
                      <span>Rate</span>
                      <span>
                        ₹{" "}
                        {item.is_manual ? (
                          <input
                            type="number"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              const newItems = [...poData.items];
                              const index = poData.items.findIndex(
                                (i) => i.id === item.id,
                              );

                              newItems[index].unit_price = value;
                              newItems[index].total =
                                value * newItems[index].ordered_qty;

                              const totalAmount = newItems.reduce(
                                (sum, i) => sum + i.total,
                                0,
                              );

                              setPoData({
                                ...poData,
                                items: newItems,
                                total_amount: totalAmount,
                              });
                            }}
                            className="border px-2 py-1 rounded w-24"
                          />
                        ) : (
                          `₹ ${item.unit_price}`
                        )}
                      </span>
                    </div>

                    {/* Qty */}
                    <div className="flex justify-between mt-2">
                      <span>Qty</span>
                      <input
                        type="number"
                        step="0.01"
                        value={item.ordered_qty}
                        onChange={(e) =>
                          updateItem(item.id, {
                            ordered_qty: parseFloat(e.target.value) || 0.0,
                          })
                        }
                        className="border px-2 py-1 w-20"
                      />
                    </div>

                    {/* Rate */}
                    <div className="flex justify-between mt-2">
                      <span>Rate</span>
                      {item.is_manual ? (
                        <input
                          type="number"
                          value={item.ordered_qty}
                          min="0"
                          onChange={(e) => {
                            const qty = parseFloat(e.target.value) || 0;
                            const newItems = [...poData.items];

                            newItems[originalIndex].ordered_qty = qty;
                            newItems[originalIndex].total =
                              qty * newItems[originalIndex].unit_price;

                            const totalAmount = newItems.reduce(
                              (sum, i) => sum + i.total,
                              0,
                            );

                            setPoData({
                              ...poData,
                              items: newItems,
                              total_amount: totalAmount,
                            });
                          }}
                          className="border px-2 py-1 rounded w-24 text-center"
                        />
                      ) : (
                        `₹ ${item.unit_price}`
                      )}
                    </div>

                    <div className="flex justify-between mt-2">
                      <span>Billable</span>
                      <input
                        type="checkbox"
                        checked={item.is_billable !== 0}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          const newItems = [...poData.items];
                          const index = poData.items.findIndex(
                            (i) => i.id === item.id,
                          );

                          newItems[index].is_billable = isChecked ? 1 : 0;
                          setPoData({ ...poData, items: newItems });
                        }}
                      />
                    </div>
                    {/* Remove */}
                    {item.is_manual && (
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="mt-3 w-full bg-red-500 text-white py-1 rounded"
                      >
                        Remove Item
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Terms & Conditions + Notes */}
        <div className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Terms & Conditions
            </label>
            <textarea
              value={poData.t_c}
              onChange={(e) => setPoData({ ...poData, t_c: e.target.value })}
              rows={3}
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Enter terms and conditions..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={poData.notes}
              onChange={(e) => setPoData({ ...poData, notes: e.target.value })}
              rows={3}
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Enter additional notes..."
            />
          </div>
        </div>

        {/* Grand Total */}
        <div className="text-right mt-6 text-sm md:text-base space-y-1">
          <div>Subtotal: ₹ {subtotal.toLocaleString("en-IN")}</div>

          <div>GST (18%): ₹ {gstAmount.toLocaleString("en-IN")}</div>

          <div className="text-lg font-bold">
            Grand Total: ₹ {grandTotal.toLocaleString("en-IN")}
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-2 items-center">
          <button
            type="submit"
            disabled={!hasQty}
            className={`mt-4 w-full py-2 rounded text-white  ${hasQty ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"}`}
          >
            Create Purchase Order
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="mt-4 w-full bg-red-600 text-white py-2 rounded hover:bg-red-700"
          >
            Cancel Purchase Order
          </button>
        </div>
      </form>

      {/* modal poup for descrption view */}
      <Modal
        isOpen={descModal}
        onRequestClose={() => setDescModal(false)}
        className="bg-white p-6 rounded shadow-lg max-w-2xl mx-auto mt-20 z-[80]"
        overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-start z-[70]"
      >
        <h2 className="text-lg font-semibold mb-4">Item Description</h2>

        <p className="text-gray-700 whitespace-pre-line">{selectedDesc}</p>

        <div className="mt-4 text-right">
          <button
            onClick={() => setDescModal(false)}
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
}
